import React, { useMemo, useRef, useState } from 'react';
import { X, Check, ChevronDown, SlidersHorizontal, AlertTriangle } from 'lucide-react';
import GameSearchModal from './GameSearchModal';
import GameForm from './GameForm';
import ConfirmDialog from './ConfirmDialog';
import { PLATFORMS, STATUSES } from '../data/constants';
import { findPossibleDuplicate } from '../utils/gameUtils';
import { useModalA11y } from '../hooks/useModalA11y';

const GameModal = ({
  isEditing,
  editingGameId,
  games = [],
  formData,
  searchQuery,
  searchResults,
  isSearching,
  showSearchResults,
  onSearchChange,
  onSelectGame,
  onFormChange,
  onToggleTag,
  onSave,
  onCancel
}) => {
  const [showDetails, setShowDetails] = useState(isEditing);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const initialSnapshot = useRef(JSON.stringify(formData));

  const isDirty = () => JSON.stringify(formData) !== initialSnapshot.current;

  const attemptClose = () => {
    if (isDirty()) setConfirmDiscard(true);
    else onCancel();
  };

  const containerRef = useModalA11y(attemptClose);

  const duplicate = useMemo(
    () => findPossibleDuplicate(games, formData.name, editingGameId),
    [games, formData.name, editingGameId]
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex sm:items-center sm:justify-center sm:p-4">
      <div ref={containerRef} tabIndex={-1} className="bg-slate-900 w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl sm:border sm:border-slate-800 overflow-y-auto flex flex-col animate-fadeIn">
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-4 py-4 flex justify-between items-center">
          <h2 className="text-lg font-bold text-white">
            {isEditing ? 'Editar Jogo' : 'Adicionar Jogo'}
          </h2>
          <button onClick={attemptClose} className="text-slate-400 active:text-white p-1" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-5 flex-1">
          <GameSearchModal
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            searchResults={searchResults}
            isSearching={isSearching}
            showResults={showSearchResults}
            onSelectGame={onSelectGame}
          />

          {duplicate && (
            <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs font-medium p-3 rounded-xl">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Já existe <b>{duplicate.name}</b> na sua biblioteca ({duplicate.platform}). Se for outra plataforma ou edição, pode continuar normalmente.</span>
            </div>
          )}

          {!showDetails ? (
            <div className="surface p-4 sm:p-5">
              <p className="eyebrow mb-4">Só o essencial</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-300 mb-1.5 text-sm font-medium">Nome do jogo</label>
                  <input value={formData.name} onChange={e => onFormChange({ ...formData, name:e.target.value })} className="input-field" placeholder="Digite ou selecione na busca" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-slate-300 mb-1.5 text-sm font-medium">Plataforma</label><select value={formData.platform} onChange={e => onFormChange({ ...formData, platform:e.target.value })} className="input-field">{PLATFORMS.map(item => <option key={item}>{item}</option>)}</select></div>
                  <div><label className="block text-slate-300 mb-1.5 text-sm font-medium">Status</label><select value={formData.status} onChange={e => onFormChange({ ...formData, status:e.target.value })} className="input-field">{STATUSES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
                </div>
              </div>
              <button onClick={() => setShowDetails(true)} className="w-full mt-5 py-3 rounded-xl border border-dashed border-white/[0.12] text-xs font-bold text-slate-400 hover:text-white hover:border-white/20 transition flex items-center justify-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Adicionar nota, comentário e outros detalhes <ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <GameForm formData={formData} onChange={onFormChange} onToggleTag={onToggleTag} />
          )}
        </div>

        <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-slate-800 px-4 py-4 flex gap-3 pb-safe">
          <button
            onClick={onSave}
            className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 text-sm"
          >
            <Check className="w-4 h-4" />
            {isEditing ? 'Salvar Alterações' : 'Adicionar Jogo'}
          </button>
          <button
            onClick={attemptClose}
            className="btn-secondary px-6 py-3 text-sm"
          >
            Cancelar
          </button>
        </div>
      </div>

      {confirmDiscard && (
        <ConfirmDialog
          title="Descartar alterações?"
          description="Você tem alterações não salvas neste jogo. Se sair agora, elas serão perdidas."
          confirmLabel="Descartar"
          cancelLabel="Continuar editando"
          danger
          onConfirm={() => { setConfirmDiscard(false); onCancel(); }}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}
    </div>
  );
};

export default GameModal;
